import React, { useMemo } from 'react';

import { dashboardBookingInquiryApis } from '@apis/next/bookingInquiryApis';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import {
  BookingInquiryItem,
  BookingInquiryTable,
  BookingInquiryTableProps,
} from '@components/BookingTable';
import { Button } from '@components/Button';
import { TagSearchInput } from '@components/Forms/TagSearchInput';
import { TagSearchInputMobxZodControl } from '@components/Forms/TagSearchInputStore';
import { PageSizeForm } from '@components/NextForms/PageSizeForm';
import { TextInput } from '@components/NextForms/TextInput';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { useSearch } from '@hooks/next/useSearch';
import { useAgencyList } from '@hooks/queries/dashboard/useAgencyList';
import { useUser } from '@hooks/queries/useUser';
import { DashboardBookingInquiry, DashboardBookingInquiryQuery } from '@models/next/BookingInquiry';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getFieldLabel } from '@utils/nextForms';
import { url } from '@utils/pages';
import { formatDateTime } from '@utils/types';
import clsx from 'clsx';
import { pick } from 'remeda';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  tagSearchInputContainer: {
    ...common.middleText,
    display: 'grid',
    rowGap: 20,
    marginTop: '1rem',
    marginBottom: '1rem',
    backgroundColor: theme.palette.grey[50],
    padding: '1rem 1rem',
  },
  searchRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr 30px) 30px repeat(2, 1fr 30px)',
    columnGap: 8,
  },
  searchTailLabel: {
    ...common.middleText,
    ...common.textGray700,
    ...common.textAlignCenter,
    paddingTop: 5,
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '0.5rem',
  },
  emptyHintContainer: {
    ...common.middleText,
    ...common.textGray400,
    textAlign: 'center',
  },
  textTrim: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const strings = createStrings({
  ja: {
    title: '予約一覧',
    tagSearchInputPlaceholder: 'D2D番号、会社名や会員番号などの検索条件を入力してください',
  },
});

function mapBookingInquiryIntoBodyColumns(
  item: DashboardBookingInquiry,
  classes: ReturnType<typeof useCommonStyles>,
): BookingInquiryItem {
  return {
    id: item.d2d_no!,
    d2dNumber: <span className={classes.whitespacePre}>{item.d2d_no}</span>,
    receptionHours: item.created_at ?? '-',
    membershipNumber: item.member_no ?? '-',
    englishName: item.english_name ?? '-',
    applicant: `${item.user?.last_name} ${item.user?.first_name}` ?? '-',
    consignee: item.consignee ?? '-',
    status: item.status ?? '-',
    memberSalesperson: item.member_salesperson ?? '-',
    csManagerChina: item.salesperson_china_last_name ?? '-',
    csManagerJapan: item.salesperson_japan_last_name ?? '-',
    agencyName: item.agency_name || '-',
  };
}

const BookingInquirySearchForm = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { search, updateSearch } = useSearch(DashboardBookingInquiryQuery);

  const initialSearch = {
    ...search,
    page: search.page ?? 1,
    page_size: search.page_size ?? 50,
  };

  const form = useForm(DashboardBookingInquiryQuery, {
    initialOutput: initialSearch,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const controller = useMemo(
    () =>
      new TagSearchInputMobxZodControl(
        pick(fields, ['d2d_no', 'company_name', 'member_no', 'email', 'fullname']),
        form,
      ),
    [],
  );

  return (
    <form
      {...form.bindForm({
        onSubmit(d) {
          updateSearch({ ...d, page: 1 });
        },
      })}
      className={classes.tagSearchInputContainer}
    >
      <TagSearchInput
        controller={controller}
        placeholder={strings.tagSearchInputPlaceholder}
        styles={{
          wrapper: {
            gridColumn: '1 / -1',
          },
        }}
      />

      <div className={classes.searchRow}>
        <div>
          <TextInput
            field={fields.created_at_after}
            inputProps={{ placeholder: getFieldLabel(fields.created_at_after) }}
          />
        </div>
        <label className={classes.searchTailLabel}>{formStrings.from}</label>

        <div>
          <TextInput
            field={fields.created_at_before}
            inputProps={{ placeholder: getFieldLabel(fields.created_at_before) }}
          />
        </div>
        <label className={classes.searchTailLabel}>{formStrings.until}</label>
      </div>

      <div className={classes.buttonContainer}>
        <Button
          color="primary"
          shape="round"
          text={formStrings.filter}
          type="submit"
          size="small"
        />
        <Button
          color="primary"
          shape="round"
          text={formStrings.reset}
          type="submit"
          size="small"
          onClick={() => {
            form.root.setOutput(empty);
          }}
        />
      </div>
    </form>
  );
};

export const BookingInquiries: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { search, updateSearch } = useSearch(DashboardBookingInquiryQuery);

  const query = {
    status: ['未対応', '対応中'] as const,
    ...search,
    page_size: search.page_size ?? 50,
    page: search.page ?? 1,
  };

  const bookingInquiries = dashboardBookingInquiryApis.useGet('/dashboard/booking-inquiry', {
    queries: {
      query: query,
    },
  });

  const agencyList = useAgencyList();

  const user = useUser();

  const items: BookingInquiryTableProps['items'] | undefined = bookingInquiries.data?.results.map(
    (item) => {
      return mapBookingInquiryIntoBodyColumns(
        {
          ...item,
          created_at: item.created_at == null ? '' : formatDateTime(item.created_at),
          english_name: item.owner_company?.english_name ?? '',
        },
        classes,
      );
    },
  );

  const handleReceptionHoursChange: BookingInquiryTableProps['onReceptionHoursChange'] = (
    value,
  ) => {
    updateSearch({
      ...search,
      page: 1,
      ordering: value === 'DESC' ? '-created_at' : 'created_at',
    });
  };

  const handleStatusChange: BookingInquiryTableProps['onStatusChange'] = (value) => {
    updateSearch({
      ...search,
      page: 1,
      status: [value],
    });
  };

  const handleCsManagerChinaChange: BookingInquiryTableProps['onCsManagerChinaChange'] = (
    value,
  ) => {
    updateSearch({
      ...search,
      page: 1,
      salesperson_china_id: value ? user.data?.id : undefined,
    });
  };

  const handleCsManagerJapanChange: BookingInquiryTableProps['onCsManagerJapanChange'] = (
    value,
  ) => {
    updateSearch({
      ...search,
      page: 1,
      salesperson_japan_id: value ? user.data?.id : undefined,
    });
  };

  const handleMemberSalespersonChange: BookingInquiryTableProps['onMemberSalespersonChange'] = (
    value,
  ) => {
    updateSearch({
      ...search,
      page: 1,
      member_salesperson_id: value ? user.data?.id : undefined,
    });
  };

  const handleAgencyIdChange: BookingInquiryTableProps['onAgencyIdChange'] = (value) => {
    updateSearch({
      ...search,
      page: 1,
      agency_id: value,
    });
  };

  return (
    <Layout
      title={strings.title}
      loading={bookingInquiries.isLoading || user.isLoading || agencyList.isLoading}
      errors={[bookingInquiries.error, user.error]}
    >
      <div className={classes.container}>
        <SectionTitle>{strings.title}</SectionTitle>

        <BookingInquirySearchForm />

        <PageSizeForm />

        <div style={{ height: 16 }} />

        <BookingInquiryTable
          initialIndex={((query.page ?? 0) - 1) * query.page_size + 1}
          items={items ?? []}
          agencyList={user.data?.role && user.data?.role !== 'FCChina' ? agencyList.data ?? [] : []}
          onReceptionHoursChange={handleReceptionHoursChange}
          onStatusChange={handleStatusChange}
          onCsManagerJapanChange={handleCsManagerJapanChange}
          onCsManagerChinaChange={handleCsManagerChinaChange}
          onMemberSalespersonChange={handleMemberSalespersonChange}
          onAgencyIdChange={handleAgencyIdChange}
          detailsURL={url('/admin/booking/InquiryDetails')}
        />

        <div style={{ height: 36 }} />
        <div className={clsx(classes.flexInline, classes.justifyEnd)}>
          <PaginationWithSearchQuery query={bookingInquiries} pageSize={query.page_size} />
        </div>
      </div>
    </Layout>
  );
};
